import { jsx } from '@emotion/react';
import React from 'react';

import Variable from '../components/Variable';
import Wrapper from '../components/Wrapper';
import { Node, NodeType, Processor } from '../types';
import { error } from '../utils/error';
import { isCustomComponent } from './utils';

export type HandleNodesParams = {
  processors: Processor[];
  nodes: Node[];
};

export type HandleNodeParams = {
  node: Node;
  index: number;
  processors: Processor[];
};

export type Payload = {
  /**
   * Array of processors being evaluated.
   */
  processors: Processor[];
};

export type ApplyProcessorsParams = Payload & {
  node: Node;
};

export const handleNodes = ({ nodes, processors }: HandleNodesParams): React.ReactNode[] | null => {
  const handled = nodes.reduce((final: React.ReactNode[], node, index) => {
    const handledNode = handleNode({ node, index, processors });
    if (handledNode) final.push(handledNode);
    return final;
  }, []);

  if (handled.length > 1) return handled;
  if (handled.length === 1) return handled;

  return null;
};

const handleNode = ({ node, index, processors }: HandleNodeParams): React.ReactNode => {
  // `applyProcessors` returns true if node was removed.
  if (applyProcessors({ node, processors })) return null;
  if (node.type === NodeType.Comment) return null;
  if (node.type === NodeType.Text) return node.content;
  if (node.type === NodeType.Variable) {
    return <Variable key={index} {...node.props} />;
  }
  if (node.type === NodeType.Element) {
    if (
      // If the component is not a string, it could not be a custom component,
      // only a React component.
      typeof node.component === 'string' &&
      isCustomComponent(node.component, node.props)
    ) {
      return (
        <Wrapper key={index} className={node.props.className ?? ''} Component={node.component} {...node.props}>
          {node.children ? handleNodes({ nodes: node.children, processors }) : null}
        </Wrapper>
      );
    }
    return jsx(
      node.component,
      { ...{ key: index, ...node.props } },
      node.children ? handleNodes({ nodes: node.children, processors }) : []
    );
    // return (
    //   <node.component
    //     {...{ key: index, ...node.props, css: node?.props?.css ?? undefined }}
    //   >
    //     {node.children
    //       ? handleNodes({ nodes: node.children, ...payload })
    //       : null}
    //   </node.component>
    // );
  }
};

const applyProcessors = ({ node, processors }: ApplyProcessorsParams): boolean => {
  if (!processors) {
    return false;
  }
  for (const proc of processors) {
    const processor = proc.processor || proc.process;
    let isMatch = false;

    // Check if test and processor are set.
    if (!proc.test || !processor)
      error(`The processor ${proc.name || '(missing name)'} needs both a "test" and a "processor" properties.`);

    // Test processor.
    try {
      /**
       * Run the tester passing node and params merged for backward
       * compatibility.
       */
      const params = { node, processors };
      isMatch = proc.test({ ...node, ...params });
    } catch (e) {
      console.warn(e);
    }
    if (!isMatch) continue;

    // Apply processor.
    try {
      /**
       * Run the processor passing node and params merged, and payload as
       * a second argument for backward compatibility.
       */
      const params = { node, processors };
      // const processed = processor({ ...node, ...params }, payload);
      const processed = processor({ ...node, ...params });
      // Return true if node was removed.
      if (!processed) return true;
      // Merge the nodes if the processor has applied changes.
      if (node !== processed) {
        /**
         * Remove props merged before process, just in case someone
         * has used the spread operator in a processor.
         */
        Object.keys(params).forEach(key => delete processed[key]);
        // Assign returned props.
        Object.assign(node, processed);
      }
    } catch (e) {
      console.error(e);
    }
  }

  // Return false if node was not removed.
  return false;
};
